import {ActionIcon, Container, Divider, Group, Tabs, TabsValue, Title} from "@mantine/core";
import {
  AllocatableLoadSettingsDataTable
} from "../../data-table/AllocatableLoadSettingsDataTable/AllocatableLoadSettingsDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {
  allocatableLoadSettingsListSelector,
  EntityObject,
  getRequiredObjectId,
  getRequiredObjectType,
  toTransportCategory,
  transportCategory,
  TransportCategory,
  useGetAllocatableLoadSettingsByObjectQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {useNavigate} from "react-router-dom";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TransportCategoryTabs} from "../../tabs/TransportCategoryTabs/TransportCategoryTabs";

export type AllocatableLoadSettingsDataTableCardProps = CardProps & {
  entity: EntityObject
  defaultCategory?: TransportCategory
}

export const AllocatableLoadSettingsDataTableCard = (props: AllocatableLoadSettingsDataTableCardProps) => {
  const {
    entity,
    defaultCategory = transportCategory.water,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const [selectedCategory, setSelectedCategory] = useState<TransportCategory>(defaultCategory)

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const selector = useMemo(() => {
    return allocatableLoadSettingsListSelector();
  }, []);

  const {allocatableLoadSettings, isLoading} = useGetAllocatableLoadSettingsByObjectQuery({
    id: getRequiredObjectId(entity),
    type: getRequiredObjectType(entity),
  }, {
    selectFromResult: (result) => ({
      ...result,
      allocatableLoadSettings: selector(result),
      isLoading: result.isLoading
    })
  });

  const waterSettings = useMemo(() => {
    return allocatableLoadSettings.filter((settings) => settings.category === transportCategory.water)
  }, [allocatableLoadSettings])

  const oilSettings = useMemo(() => {
    return allocatableLoadSettings.filter((settings) => settings.category === transportCategory.oil)
  }, [allocatableLoadSettings])

  const onNewClick = useCallback(() => {
    const objectId = getRequiredObjectId(entity)
    const objectType = getRequiredObjectType(entity)

    navigate(`/allocatable-load-settings/form?objectId=${objectId}&objectType=${objectType}`, {
      state: {
        id: undefined,
        formDefaults: {
          category: selectedCategory
        }
      }
    });
  }, [navigate, selectedCategory, entity])

  const onTabChange = useCallback((value: TabsValue) => {
    if (value) {
      setSelectedCategory(toTransportCategory(value))
    }
  }, [])

  return (
    <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>

      <Group position="apart" pb="sm">
        <Title order={4}>{t("common.entity.allocatableloadsettings.plural", "Allocatable Load Settings")}</Title>
        <Group spacing={4} position="left" noWrap>
          <HasAccess permissions={["edit"]}>
            <ActionIcon color="green" onClick={() => onNewClick()}>
              <IconPlus size={16}/>
            </ActionIcon>
          </HasAccess>
        </Group>
      </Group>

      <Divider pb="sm"/>

      <Tabs
        defaultValue={defaultCategory.code}
        onTabChange={onTabChange}>

        <TransportCategoryTabs/>

        <Tabs.Panel value={transportCategory.water.code}>
          <Container p="md" fluid={true}>
            <AllocatableLoadSettingsDataTable
              withBorder
              records={waterSettings}
              fetching={isLoading}
            />
          </Container>
        </Tabs.Panel>

        <Tabs.Panel value={transportCategory.oil.code}>
          <Container p="md" fluid={true}>
            <AllocatableLoadSettingsDataTable
              withBorder
              records={oilSettings}
              fetching={isLoading}
            />
          </Container>
        </Tabs.Panel>

      </Tabs>
    </Card>
  )
}
